import React from "react";
import { Dialog, DialogProps, Drawer, DrawerProps } from "@mui/material";
import { useIsMobile } from "shared/hooks";
import styles from "./styles.module.scss";

type Pickable = "open" | "onClose" | "children" | "fullWidth" | "maxWidth" | "scroll";

interface ResponsiveDialogProps extends Pick<DialogProps, Pickable> {
	/**
	 * Props extras passados ao Dialog (desktop). Ignorado em mobile.
	 */
	dialogProps?: Omit<DialogProps, Pickable>;
	/**
	 * Props extras passados ao Drawer (mobile). Ignorado em desktop.
	 */
	drawerProps?: Omit<DrawerProps, "open" | "onClose" | "children" | "anchor">;
}

/**
 * Modal responsivo padrao WhatsApp-like:
 * - Desktop (≥ 769px): Dialog centrado (padrao MUI)
 * - Mobile (≤ 768px): Drawer ancorado embaixo, com cantos superiores
 *   arredondados e altura maxima 90vh (deixando preview do que esta atras)
 *
 * Use no lugar de <Dialog> direto p/ que o conteudo funcione bem em mobile.
 * O drag handle no topo (barrinha cinza) sinaliza affordance de "puxar".
 *
 * Importante: por compatibilidade, em mobile o conteudo ainda usa
 * DialogTitle / DialogContent / DialogActions normais — apenas o
 * container externo muda. Estilos especificos de mobile devem ser
 * aplicados via :global ou nested SCSS no contexto do drawer.
 *
 * @example
 *   <ResponsiveDialog open={open} onClose={onClose}>
 *     <DialogTitle>...</DialogTitle>
 *     <DialogContent>...</DialogContent>
 *     <DialogActions>...</DialogActions>
 *   </ResponsiveDialog>
 */
export const ResponsiveDialog: React.FC<ResponsiveDialogProps> = ({
	open,
	onClose,
	children,
	fullWidth,
	maxWidth,
	scroll,
	dialogProps,
	drawerProps,
}) => {
	const isMobile = useIsMobile();

	if (isMobile) {
		return (
			<Drawer
				anchor="bottom"
				open={open}
				onClose={onClose}
				PaperProps={{
					className: styles.drawerPaper,
				}}
				{...drawerProps}
			>
				<div
					className={styles.dragHandle}
					aria-hidden
				/>
				<div className={styles.drawerContent}>{children}</div>
			</Drawer>
		);
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth={fullWidth}
			maxWidth={maxWidth}
			scroll={scroll}
			{...dialogProps}
		>
			{children}
		</Dialog>
	);
};
